import React from "react";
import {Alert, FormControlLabel, MenuItem, Switch, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {PaidOutlined, ReportProblemOutlined, WorkOutlineOutlined} from "@mui/icons-material";
import type {GridColDef, GridDataSource} from "@mui/x-data-grid-premium";
import {useGridApiRef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {searchInputProps} from "~/experiments/components/search-field/searchFieldSlotProps.ts";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import type {ContractLifecycle, ContractRow} from "./contractExplorerTypes.ts";
import {LIFECYCLE_LABEL, LIFECYCLES} from "./contractExplorerTypes.ts";
import {daysUntil, formatDate, formatMoney, typeLabel} from "./contractExplorerFormat.ts";
import {fetchContractPage, useContractSummaryQuery} from "./useContractExplorerQueries.ts";
import type {ContractListParams} from "./useContractListParams.ts";
import {PAGE_SIZE_OPTIONS, toOrdering, toSortModel} from "./useContractListParams.ts";
import {GapTags} from "./components/GapTags.tsx";
import {LifecycleTag} from "./components/LifecycleTag.tsx";

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
    bagId: number;
    params: ContractListParams;
    update: (patch: Partial<ContractListParams>) => void;
};

/**
 * The bundles of the databag: KPI pills, the lifecycle control and the
 * needs-attention scope above one server-paged grid. Every setting lives in
 * the URL, so coming back from a bundle restores the same list.
 */
export const ContractListView: React.FC<Props> = ({bagId, params, update}) => {
    const apiRef = useGridApiRef();
    const {search, lifecycle, type, needsAttention, ordering, page, pageSize} = params;
    const [searchInput, setSearchInput] = React.useState(search);
    const [total, setTotal] = React.useState<number | undefined>(undefined);
    const [error, setError] = React.useState<string | null>(null);
    const [loadingRows, setLoadingRows] = React.useState(true);
    const {data: summary, error: summaryError} = useContractSummaryQuery(bagId);
    const currency = summary?.currency ?? '';
    const asOf = summary?.as_of ?? '';

    React.useEffect(() => {
        if (searchInput.trim() === search) return;
        const timer = setTimeout(() => update({search: searchInput.trim()}), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput, search, update]);

    const dataSource = React.useMemo<GridDataSource>(() => ({
        getRows: async ({start, end}) => {
            const offset = typeof start === 'number' ? start : 0;
            setLoadingRows(true);
            try {
                const result = await fetchContractPage({
                    bagId, offset, limit: Math.max(end - offset + 1, 1), search: search || undefined,
                    lifecycle: lifecycle ?? undefined, documentType: type ?? undefined, needsAttention, ordering,
                });
                setTotal(result.total);
                setError(null);
                return {rows: result.rows, rowCount: result.total};
            } finally {
                setLoadingRows(false);
            }
        },
    }), [bagId, search, lifecycle, type, needsAttention, ordering]);

    // Search and the controls above the grid live outside it, so it has to be
    // told to fetch again when they change.
    const firstRender = React.useRef(true);
    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        apiRef.current?.dataSource?.fetchRows();
    }, [apiRef, search, lifecycle, type, needsAttention, ordering]);

    // Every cell is a flex box, so one-line and two-line cells centre on the same row line. Type, lifecycle,
    // expiry and spend are bounded and get fixed widths; the names share the rest, fitting a 1400px grid.
    const columns = React.useMemo<GridColDef<ContractRow>[]>(() => [
        {
            field: 'title', headerName: 'Bundle', flex: 2, minWidth: 260, display: 'flex',
            renderCell: ({row}) => (
                <span className="ci-cname">
                    <b title={row.title}>{row.title}</b>
                    <span className="ci-csub">
                        <span className="ci-id">#{row.bundle_id}</span>
                        · {row.document_count} source {row.document_count === 1 ? 'document' : 'documents'}
                    </span>
                </span>
            ),
        },
        {field: 'supplier_name', headerName: 'Supplier', flex: 1.1, minWidth: 160, display: 'flex',
            renderCell: ({row}) => (row.supplier_name
                ? <span className="ci-cell-text" title={row.supplier_name}>{row.supplier_name}</span>
                : <span className="ci-dash">—</span>)},
        {field: 'document_type', headerName: 'Type', width: 180, display: 'flex',
            renderCell: ({row}) => <span className="ci-cell-text">{typeLabel(row.document_type)}</span>},
        {field: 'lifecycle', headerName: 'Lifecycle', width: 110, sortable: false, display: 'flex',
            renderCell: ({row}) => <LifecycleTag lifecycle={row.lifecycle}/>},
        {field: 'buying_entities', headerName: 'Buying entities', flex: 1.1, minWidth: 160, sortable: false,
            display: 'flex',
            renderCell: ({row}) => (row.buying_entities.length
                ? <span className="ci-cell-text" title={row.buying_entities.join(' · ')}>
                    {row.buying_entities.length === 1 ? row.buying_entities[0] : `${row.buying_entities.length} entities`}
                </span>
                : <span className="ci-dash" title="No party is stated as the buyer">—</span>)},
        {
            field: 'ends_on', headerName: 'Expires', width: 130, display: 'flex',
            renderCell: ({row}) => {
                if (!row.ends_on) return <span className="ci-csub">Open-ended</span>;
                const days = daysUntil(row.ends_on, asOf);
                return (
                    <span className="ci-cname">
                        <span>{formatDate(row.ends_on)}</span>
                        {days !== null && <span className={`ci-csub${row.lifecycle === 'expiring' ? ' is-soon' : ''}`}>
                            {days < 0 ? `${-days} days ago` : `in ${days} days`}
                        </span>}
                    </span>
                );
            },
        },
        {field: 'linked_spend', headerName: 'Spend under contract', width: 190, align: 'right', headerAlign: 'right',
            display: 'flex',
            renderCell: ({row}) => <span className="ci-mono">{formatMoney(row.linked_spend, currency)}</span>},
        {field: 'weighted_saving', headerName: 'Expected saving', width: 170, align: 'right', headerAlign: 'right',
            description: "Each opportunity's estimated saving times its confidence", display: 'flex',
            renderCell: ({row}) => (row.weighted_saving
                ? <span className="ci-mono">{formatMoney(row.weighted_saving, currency)}</span>
                : <span className="ci-dash" title="No leakage found">—</span>)},
        {field: 'gaps', headerName: 'Needs attention', flex: 1.2, minWidth: 220, sortable: false, display: 'flex',
            renderCell: ({row}) => <GapTags gaps={row.gaps}/>},
    ], [asOf, currency]);

    const kpis: SitKpiPill[] = summary ? [
        {icon: <WorkOutlineOutlined/>, tone: 'info', label: 'Bundles',
            value: <>{summary.bundles}<span className="ci-kpi-of"> from {summary.documents} documents</span></>},
        {icon: <PaidOutlined/>, tone: 'ok', label: 'Spend under contract',
            value: formatMoney(summary.linked_spend, summary.currency)},
        {icon: <ReportProblemOutlined/>, tone: 'warn', label: 'Needs attention', value: summary.needs_attention,
            help: 'Bundles linkage could not finish: a supplier or buying entity not matched, no versions, or a failure.'},
    ] : [];
    const lifecycleOptions = LIFECYCLES.filter(l => (summary?.lifecycle_counts[l] ?? 0) > 0);
    const loadError = error ?? summaryError?.message ?? null;
    const latest = summary?.latest_import;

    return <>
        {kpis.length > 0 && <SitKpiHeader items={kpis}/>}
        {summary && !summary.bundles && !latest && (
            <Alert severity="info">No bundles yet.</Alert>
        )}
        {loadError && <Alert severity="error">Could not load the bundles: {loadError}</Alert>}
        <div className="ci-filters">
            <TextField
                className="ci-search" type="search" size="small" placeholder="Search title, supplier or bundle id…"
                value={searchInput} onChange={e => setSearchInput(e.target.value)}
                slotProps={{htmlInput: searchInputProps('contract-search', 'Search bundles')}}/>
            <ToggleButtonGroup size="small" exclusive className="ci-segmented" value={lifecycle ?? 'all'}
                               onChange={(_e, next: ContractLifecycle | 'all' | null) =>
                                   next && update({lifecycle: next === 'all' ? null : next})}>
                <ToggleButton value="all">All</ToggleButton>
                {lifecycleOptions.map(l => (
                    <ToggleButton key={l} value={l}>{LIFECYCLE_LABEL[l]} · {summary?.lifecycle_counts[l]}</ToggleButton>
                ))}
            </ToggleButtonGroup>
            <TextField select size="small" className="ci-typefilter" value={type ?? ''}
                       onChange={e => update({type: e.target.value || null})}
                       slotProps={{select: {displayEmpty: true}}}>
                <MenuItem value="">All types</MenuItem>
                {(summary?.type_counts ?? []).filter(t => t.document_type).map(t => (
                    <MenuItem key={t.document_type} value={t.document_type}>{typeLabel(t.document_type)} ({t.count})</MenuItem>
                ))}
            </TextField>
            <FormControlLabel
                control={<Switch size="small" checked={needsAttention} onChange={(_e, on) => update({needsAttention: on})}/>}
                label="Needs attention"/>
            <span className="ci-rowcount">{total === undefined ? '' : `${total} bundles`}</span>
        </div>
        <div className="ci-table-wrap">
            <CustomDataGrid
                apiRef={apiRef}
                columns={columns}
                dataSource={dataSource}
                dataSourceCache={null}
                virtualized
                rowSize="medium"
                loading={loadingRows}
                rowCount={total ?? 0}
                pagination
                paginationMode="server"
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                paginationModel={{page, pageSize}}
                onPaginationModelChange={model => update({page: model.page, pageSize: model.pageSize})}
                sortingMode="server"
                onSortModelChange={model => update({ordering: toOrdering(model)})}
                initialState={{sorting: {sortModel: toSortModel(ordering)}}}
                disableColumnFilter
                onDataSourceError={e => setError(e.message)}
                onRowClick={({row}) => update({contract: row.id})}
                getRowClassName={() => 'ci-rowlink'}
                disableRowSelectionOnClick
            />
        </div>
    </>;
};
